// ────────────────────────────────────────────────────────────
// DEAL DESK — request queues client (tract-scoped)
// ────────────────────────────────────────────────────────────
// Queues belong to the TRACT: doc numbers and branch traces are
// title work, not deal work. These hit live D1 through
// /api/deal-desk/* even while tract data is in fixture mode. In
// local vite dev (no Functions runtime) calls fail; the UI shows
// the queue as unavailable rather than pretending.

import { useCallback, useEffect, useState } from 'react';
import { authHeaders } from '../../lib/authHeaders';

export interface IngestRequest {
  id: string;
  doc_number: string;
  status: 'pending' | 'done' | 'rejected';
  requested_at: string;
  note: string | null;
}

export interface ResearchTask {
  id: string;
  kind: string;
  subject: string;
  detail: string | null;
  status: 'pending' | 'done' | 'rejected';
  requested_at: string;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!r.ok) {
    let msg = await r.text();
    try { msg = JSON.parse(msg).error ?? msg; } catch { /* raw text */ }
    throw new Error(`API ${r.status}: ${msg}`);
  }
  return r.json() as Promise<T>;
}

export interface TractRequestsState {
  available: boolean;
  ingest: IngestRequest[];
  research: ResearchTask[];
  queueDoc(docNumber: string): Promise<void>;
  queueBranchTrace(subject: string, detail: string): Promise<void>;
  error: string | null;
}

export function useTractRequests(trs: string | undefined): TractRequestsState {
  const [available, setAvailable] = useState(true);
  const [ingest, setIngest] = useState<IngestRequest[]>([]);
  const [research, setResearch] = useState<ResearchTask[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!trs) return;
    try {
      const q = `tract=${encodeURIComponent(trs)}`;
      const [a, b] = await Promise.all([
        api<{ requests: IngestRequest[] }>(`/api/deal-desk/ingest-requests?${q}`),
        api<{ tasks: ResearchTask[] }>(`/api/deal-desk/research-tasks?${q}`),
      ]);
      setIngest(a.requests);
      setResearch(b.tasks);
      setAvailable(true);
    } catch {
      setAvailable(false);
    }
  }, [trs]);

  useEffect(() => { void refresh(); }, [refresh]);

  const queueDoc = useCallback(async (docNumber: string) => {
    if (!trs) return;
    setError(null);
    try {
      await api(`/api/deal-desk/ingest-requests`, {
        method: 'POST',
        body: JSON.stringify({ tract: trs, docNumber }),
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
      throw e;
    }
  }, [trs, refresh]);

  const queueBranchTrace = useCallback(async (subject: string, detail: string) => {
    if (!trs) return;
    setError(null);
    try {
      await api(`/api/deal-desk/research-tasks`, {
        method: 'POST',
        body: JSON.stringify({ tract: trs, kind: 'branch_trace', subject, detail }),
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
      throw e;
    }
  }, [trs, refresh]);

  return { available, ingest, research, queueDoc, queueBranchTrace, error };
}
